export type DashboardPeriod =
  | 'Esta semana'
  | 'Este mês'
  | 'Último trimestre'
  | 'Este ano'
  | 'Últimos 12 meses'

export interface DashboardSummary {
  totalLeads: number
  totalDeals: number
  receitaMensal: number
  taxaConversao: number
  dealsFechadosMes: number
  pipelineValue: number
  pipelineCount: number
  lostDeals: number
  dealsByStage: { stage: string; count: number; value: number }[]
}

export type ClosingType = 'DIRETA' | 'RECORRENTE' | 'ALTERNATIVO'

export interface DashboardClosedDeal {
  id: string
  nome: string
  empresa: string
  owner: string
  servico: string | null
  dataFechamento: string | null
  firstPaymentDate: string | null
  tipo: ClosingType
  tipoLabel: string
  parceiro: string | null
  valor: number
  valorMensal: number
  valorSetup: number
  prazoMeses: number | null
}

export interface DashboardClosingTypeTotals {
  tipo: ClosingType
  label: string
  count: number
  total: number
  monthly: number
}

export interface DashboardSales {
  totalRevenue: number
  totalWon: number
  avgTicket: number
  totalMonthly?: number
  totalSetup?: number
  avgMonthly?: number
  byType?: DashboardClosingTypeTotals[]
  closedDeals?: DashboardClosedDeal[]
  monthlyData: { month: string; count: number; revenue: number; monthly?: number }[]
}

export interface DashboardConversion {
  totalLeads: number
  convertedLeads: number
  conversionRate: number
  leadsByStatus: { status: string; count: number }[]
}

export interface DashboardCommissions {
  totalCentavos: number
  totalCount: number
  byStatus: { status: string; count: number; totalCentavos: number }[]
  topBeneficiaries: { userId: string; name: string; totalCentavos: number; count: number }[]
}

export interface DashboardAdmin {
  totalBdrs: number
  totalClosers: number
  dealsPerMonth: number
  teamMembers: { id: string; name: string; role: 'bdr' | 'closer'; deals: number; revenue: number; goal: number; achieved: number }[]
  pipelineByCloser: { name: string; count: number; value: number }[]
  monthlyGoal: number
  monthlyAchieved: number
}

export interface DashboardRetention {
  totalAccounts: number
  churnRate: number
  npsAvg: number
  mrrTotal: number
  greenCount: number
  yellowCount: number
  redCount: number
  healthScoresAvailable?: boolean
  message?: string
  accounts: {
    id: string
    companyName: string
    healthScore: 'verde' | 'amarelo' | 'vermelho'
    mrr: number
    npsScore?: number
    lastCheckIn?: string
    csm: string
    startDate: string
  }[]
}

export interface DashboardOpsHealth {
  available?: boolean
  placeholder?: boolean
  message?: string
  slaCompliance: number | null
  avgResponseTime: number | null
  systemUptime: number | null
  activeAlerts: number
  tasksCompletedToday: number
  tasksTotalToday: number
  overdueTasks: number
  metrics: { label: string; value: number; target: number; unit: string; status: 'good' | 'warning' | 'critical' }[]
}

export interface DashboardLatestDeals {
  id: string
  nome: string
  valor: number
  etapa: string
  dataFechamento: string | null
  createdAt: string
  empresa: string
  owner: string
}[]

export interface DashboardRecentActivity {
  id: string
  type: string
  description: string
  createdAt: string
  entityName: string
}[]

export interface DashboardData {
  summary: DashboardSummary | null
  sales: DashboardSales | null
  conversion: DashboardConversion | null
  bdr: DashboardConversion | null
  commissions: DashboardCommissions | null
  admin: DashboardAdmin | null
  retention: DashboardRetention | null
  opsHealth: DashboardOpsHealth | null
  latestDeals: DashboardLatestDeals[] | null
  recentActivity: DashboardRecentActivity[] | null
}

export const emptyDashboardData: DashboardData = {
  summary: null,
  sales: null,
  conversion: null,
  bdr: null,
  commissions: null,
  admin: null,
  retention: null,
  opsHealth: null,
  latestDeals: null,
  recentActivity: null,
}
